/**
 * "Is this subfield multi-valued?" — the ONE answer, for the renderer and the
 * codec alike.
 *
 * CZNA-567. Before this file the two halves disagreed. The renderer asked
 * `inputType === 'multi-record-picker' || inputType === 'multi-vocabulary'
 * || sf.multiValue`; the codec (`genericFormToProposalData`,
 * `genericProposalDataToForm`, `formTypes.createEmptyFieldInstance`) asked
 * `sf.multiValue` and nothing else. A subfield carrying only the `inputType`
 * therefore rendered a perfect chip list and then persisted ZERO rows — the
 * multi renderer writes `SubfieldValue.values[]` and never `value`, so the
 * codec's single-value branch found an empty string and emitted nothing. The
 * subfield vanished from the saved MARC without a word (CZNA-378, finding 1).
 *
 * That shape was not hypothetical: the visual editor's `applyInputKind` used to
 * DELETE `multiValue` on every kind, so every multi-value subfield an admin
 * created was born unsaveable, and merely opening a working one and saving it
 * stripped the flag off. CZNA-378 stopped the editor doing that; this file
 * removes the trap itself by making the two readings the same reading.
 *
 * `inputType` is the source of truth and `multiValue` a legacy alias, not the
 * other way round: `inputType` is the key the admin actually sets (via the
 * "Druh vstupu" select), it discriminates WHICH multi component renders, and
 * every shipped multi subfield already carries it. `multiValue` stays accepted
 * because hand-authored configs and the free-text chip list (TASK-110, a
 * `multiValue` with no picker backing) use it on its own.
 *
 * Consequence worth stating, because it is what makes a data migration
 * unnecessary: every kind `applyInputKind` can emit writes one of the three
 * `inputType`s below, so a stored config that the old editor stripped
 * `multiValue` from is repaired by this predicate alone — the flag was the
 * redundant half.
 */
/**
 * The `inputType` values that mean "several values live in
 * `SubfieldValue.values[]`". All four render through
 * `MultiSelectVocabularySubfield`; they differ only in what backs the picker
 * (authority records / vocabulary terms / nothing, i.e. free text) and, for free
 * text, whether one value is a line or a multi-line note (`multi-textarea`,
 * CZNA-1646 — `678 $a` and `665 $a` repeat in the corpus and are notes).
 */
export var MULTI_VALUE_INPUT_TYPES = [
    'multi-record-picker',
    'multi-vocabulary',
    'multi-text',
    'multi-textarea',
];
/**
 * True if this subfield's values are carried in `SubfieldValue.values[]`
 * rather than `SubfieldValue.value`.
 *
 * Every layer that branches on multi-vs-single MUST go through here. A second
 * hand-written reading of either key is how the two halves drifted apart in the
 * first place.
 */
/*
 * Returns a plain `boolean`, NOT a `sf is T` type predicate. A predicate was
 * tried: because the argument already HAS the asserted type, TS narrows the
 * false branch to `never`, and `DynamicFieldPanel`'s early-return style then
 * loses `sf` for every single-value input below the multi check (10 errors).
 * A predicate that narrows nothing is worth nothing.
 */
export var isMultiValueSubfield = function (sf) {
    // `some` rather than `indexOf`: `inputType` is widened to `string` on purpose
    // (see above), and `indexOf` would need a cast asserting that an arbitrary
    // string — `undefined` included — is a member of the schema union. The typo
    // gate lives in the array's own type, so the cast bought nothing but a lie.
    return !!sf && (sf.multiValue === true
        || MULTI_VALUE_INPUT_TYPES.some(function (t) { return t === sf.inputType; }));
};
